import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { $$toast } from '@doger/ui/components/ui/toast';
import { useAppForm } from '@doger/ui/form';
import { $$api, ErrorMatcher } from '@doger/core/shared/api';

import styles from './forgot-password-form.module.scss';

type ForgotPasswordFormProps = {
  className?: string;

  onSuccess?: () => void;
};

export const ForgotPasswordForm = ({
  className,
  onSuccess,
}: ForgotPasswordFormProps) => {
  const { t } = useTranslation('core');

  const forgotPasswordSchema = z.object({
    email: z.email(t('validation.auth.emailInvalid')),
  });
  const forgotPassword = useMutation({
    mutationFn: async ({ email }: z.infer<typeof forgotPasswordSchema>) =>
      $$api.requestPasswordReset({
        email,
      }),

    onSuccess: () => {
      $$toast.success(t('auth.forgotPassword.successMessage'));
      onSuccess?.();
    },

    onError: (error) => {
      if (
        ErrorMatcher.isAppError(error, ['TOO_MANY_PASSWORD_RESET_REQUESTS'])
      ) {
        $$toast.error(error.response!.data.message);

        return;
      }

      $$toast.error(t('auth.forgotPassword.errorMessage'));
    },
  });

  const form = useAppForm({
    defaultValues: {
      email: '',
    },
    onSubmit: async ({ value, formApi }) => {
      await forgotPassword.mutateAsync(value);

      formApi.reset();
    },
    validators: {
      onSubmit: forgotPasswordSchema,
      onChange: forgotPasswordSchema,
    },
  });

  return (
    <form
      name='forgot-password-form'
      className={className}
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <div className={styles.fields}>
        <form.AppForm>
          <form.AppField
            name='email'
            children={(field) => (
              <field.TextField
                label={t('auth.forgotPassword.emailLabel')}
                variant='contained'
                type='email'
                placeholder={t('auth.forgotPassword.emailPlaceholder')}
                autoComplete='email'
              />
            )}
          />
        </form.AppForm>
      </div>

      <form.AppForm>
        <form.SubmitButton
          variant='contained'
          size='lg'
          palette='primary'
          children={({ isSubmitting }) =>
            isSubmitting
              ? t('auth.forgotPassword.submitting')
              : t('auth.forgotPassword.submitButton')
          }
        />
      </form.AppForm>
    </form>
  );
};
